import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { TERMS_VERSION, formatLegalVersion } from '../config/legalVersions';

const TermsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      padding: '0',
    },
    header: {
      backgroundColor: '#22B8E0',
      padding: '24px 20px',
      position: 'sticky',
      top: 0,
      zIndex: 100,
    },
    headerContent: {
      maxWidth: '800px',
      margin: '0 auto',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    },
    backButton: {
      background: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      borderRadius: '8px',
      padding: '8px 16px',
      color: 'white',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    headerTitle: {
      color: 'white',
      fontSize: '24px',
      fontWeight: '700',
      margin: 0,
      flex: 1,
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '32px 20px',
    },
    lastUpdated: {
      color: '#64748b',
      fontSize: '14px',
      marginBottom: '24px',
    },
    section: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '20px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    sectionTitle: {
      color: '#1e293b',
      fontSize: '20px',
      fontWeight: '600',
      marginBottom: '16px',
      paddingBottom: '12px',
      borderBottom: '2px solid #22B8E0',
    },
    paragraph: {
      color: '#475569',
      fontSize: '15px',
      lineHeight: '1.7',
      marginBottom: '16px',
    },
    list: {
      color: '#475569',
      fontSize: '15px',
      lineHeight: '1.7',
      paddingLeft: '20px',
      marginBottom: '16px',
    },
    listItem: {
      marginBottom: '8px',
    },
    strong: {
      color: '#1e293b',
      fontWeight: '600',
    },
    warningBox: {
      backgroundColor: '#fef3c7',
      borderRadius: '8px',
      padding: '16px',
      borderLeft: '4px solid #f59e0b',
      marginBottom: '16px',
    },
    infoBox: {
      backgroundColor: '#f0f9ff',
      borderRadius: '8px',
      padding: '16px',
      borderLeft: '4px solid #22B8E0',
    },
    link: {
      color: '#22B8E0',
      textDecoration: 'none',
    },
    footer: {
      textAlign: 'center',
      padding: '24px 20px',
      color: '#94a3b8',
      fontSize: '14px',
    },
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <button style={styles.backButton} onClick={() => navigate(-1)}>
            <span>&larr;</span> {t('common.back')}
          </button>
          <h1 style={styles.headerTitle}>{t('legal.terms_title')}</h1>
        </div>
      </header>

      <main style={styles.content}>
        <p style={styles.lastUpdated}>
          Version {TERMS_VERSION} · Last updated: {formatLegalVersion(TERMS_VERSION)}
        </p>

        {/* Agreement */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>1. Agreement to Terms</h2>
          <p style={styles.paragraph}>
            By accessing or using <strong style={styles.strong}>ScrollEd</strong> (the "Service"), you agree to be
            bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Service.
          </p>
          <p style={styles.paragraph}>
            These Terms apply to all users, visitors, and others who access or use the Service worldwide.
            By accessing or using the Service (including registering an account or signing in via Google), you represent,
            warrant, and declare that you are at least 18 years of age (or the age of legal majority in your jurisdiction).
            If you are between 13 and 18 years old, you may use the Service only under the direct supervision of a parent
            or legal guardian who agrees to be bound by these Terms.
          </p>
          <p style={styles.paragraph}>
            <strong style={styles.strong}>Service Provider:</strong> ScrollEd is operated by an independent individual
            operator. Questions, legal notices,
            or requests regarding the operator may be directed to{' '}
            <a href="mailto:support@scrolled.eu" style={styles.link}>support@scrolled.eu</a>.
          </p>
          <p style={styles.paragraph}>
            <strong style={styles.strong}>Global Access & Local Laws:</strong> The Service is accessible globally.
            You are solely responsible for ensuring that your access to and use of the Service complies with all local laws
            and regulations applicable in your country of residence.
          </p>
        </section>

        {/* Description of Service */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>2. Description of Service</h2>
          <p style={styles.paragraph}>
            ScrollEd is an educational platform that transforms PDF documents into engaging,
            bite-sized learning content using artificial intelligence. Our Service includes:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>PDF document upload and processing</li>
            <li style={styles.listItem}>AI-powered content generation (summaries, learning cards, quizzes)</li>
            <li style={styles.listItem}>Progress tracking and learning analytics</li>
            <li style={styles.listItem}>Entity and concept extraction</li>
          </ul>
          <p style={styles.paragraph}>
            We may modify, suspend, or discontinue all or part of the Service for legal, security, technical, or
            operational reasons. We will provide reasonable notice where practicable and any notice, refund, remedy,
            or continued-access right required by mandatory law.
          </p>
        </section>

        {/* User Accounts */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>3. User Accounts</h2>
          <p style={styles.paragraph}>
            <strong style={styles.strong}>3.1 Account Creation</strong>
          </p>
          <p style={styles.paragraph}>
            To use certain features of the Service, you must create an account. You agree to:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Provide accurate, current, and complete information</li>
            <li style={styles.listItem}>Maintain and promptly update your account information</li>
            <li style={styles.listItem}>Maintain the security of your password</li>
            <li style={styles.listItem}>Take reasonable responsibility for activity you authorise or cause under your account</li>
            <li style={styles.listItem}>Notify us immediately of any unauthorized access</li>
          </ul>

          <p style={styles.paragraph}>
            <strong style={styles.strong}>3.2 Account Termination</strong>
          </p>
          <p style={styles.paragraph}>
            We may suspend or terminate an account for a material or repeated breach of these Terms, a credible
            security risk, unlawful use, or where required by law. Where reasonably possible, we will give notice and
            an opportunity to remedy the issue or contact us at{' '}
            <a href="mailto:support@scrolled.eu" style={styles.link}>support@scrolled.eu</a>. Immediate action may be
            taken where delay would create legal, security, or third-party harm. Suspension or termination does not
            remove rights or remedies that applicable law makes mandatory.
          </p>
        </section>

        {/* User Content */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>4. User Content</h2>
          <p style={styles.paragraph}>
            <strong style={styles.strong}>4.1 Your Documents</strong>
          </p>
          <p style={styles.paragraph}>
            You retain all rights to the documents you upload to ScrollEd. By uploading content, you grant
            the ScrollEd operator a limited, non-exclusive licence to host, reproduce, transmit, analyse, transform,
            and store that content only as reasonably necessary to provide, secure, maintain, and support the Service,
            comply with binding law, and handle your requests. That licence may be exercised through contracted
            processors and subprocessors only for those purposes. It does not transfer ownership of your source content.
          </p>

          <p style={styles.paragraph}>
            <strong style={styles.strong}>4.2 Content Ownership & Copyright Warranties</strong>
          </p>
          <p style={styles.paragraph}>
            You represent, warrant, and guarantee that you own or hold all necessary copyrights, licences, permissions,
            consents, and lawful authority for any document, text, or file you upload to or process through the Service.
            Specifically, you warrant that:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>You have the explicit legal right to submit the document for automated extraction and AI processing</li>
            <li style={styles.listItem}>Your upload does not infringe any third-party copyright, trademark, trade secret, privacy, or proprietary right</li>
            <li style={styles.listItem}>You have a lawful basis to process any personal data contained within the document</li>
            <li style={styles.listItem}>The document does not breach any contract, duty of confidentiality, or applicable law</li>
          </ul>
          <p style={styles.paragraph}>
            ScrollEd acts strictly as a passive, automated technical processing service operating at your direct request.
            You assume full legal responsibility and liability for all content you upload. You agree to indemnify and hold
            harmless the ScrollEd operator against any third-party claims, legal fees, or damages arising from your upload of
            infringing or unauthorised content.
          </p>

          <div style={styles.warningBox}>
            <p style={{ ...styles.paragraph, marginBottom: 0 }}>
              <strong style={styles.strong}>Important:</strong> Do not upload confidential, sensitive,
              or proprietary documents unless you are authorised to do so. The Service is not designed or approved for
              health, biometric, genetic, criminal-record, or other special-category personal data, or sensitive records
              concerning children. Do not upload such data. Document content is processed by external AI services to
              generate educational materials.
            </p>
          </div>
          <p style={styles.paragraph}>
            <strong style={styles.strong}>4.3 Reports & Removal</strong>
          </p>
          <p style={styles.paragraph}>
            To report content or use that you reasonably believe is unlawful or infringes your rights, email{' '}
            <a href="mailto:support@scrolled.eu" style={styles.link}>support@scrolled.eu</a> with enough information to
            identify the material, explain the concern, and contact you. We may preserve relevant evidence, restrict
            processing, or remove material where reasonably necessary, while respecting applicable procedural and
            consumer rights.
          </p>
        </section>

        {/* Intellectual Property */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>5. Intellectual Property</h2>
          <p style={styles.paragraph}>
            <strong style={styles.strong}>5.1 Our Intellectual Property</strong>
          </p>
          <p style={styles.paragraph}>
            The Service, including its original content (excluding user content), features, and functionality, is owned
            by or licensed to the individual ScrollEd operator and is protected by applicable copyright, trademark,
            database-right, and other intellectual-property laws.
          </p>

          <p style={styles.paragraph}>
            <strong style={styles.strong}>5.2 Generated Content</strong>
          </p>
          <p style={styles.paragraph}>
            Educational content generated by our AI from your documents is provided for your personal,
            non-commercial use. You may use, share, and export this generated content for educational
            purposes, subject to any rights in the original uploaded material. AI output may not be unique, may resemble
            output generated for others, and may not qualify for intellectual-property protection in every jurisdiction.
            ScrollEd does not grant rights in third-party material and does not guarantee that an output is free of
            third-party claims.
          </p>
        </section>

        {/* Acceptable Use */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>6. Acceptable Use Policy</h2>
          <p style={styles.paragraph}>
            You agree NOT to use the Service to:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Upload illegal, harmful, or offensive content</li>
            <li style={styles.listItem}>Upload prohibited special-category, criminal-record, or sensitive children's data</li>
            <li style={styles.listItem}>Use the Service for emergency, medical, legal, financial, employment, credit, admissions, or other high-impact decisions</li>
            <li style={styles.listItem}>Violate any applicable laws or regulations</li>
            <li style={styles.listItem}>Infringe on the intellectual property rights of others</li>
            <li style={styles.listItem}>Attempt to gain unauthorized access to our systems</li>
            <li style={styles.listItem}>Interfere with or disrupt the Service</li>
            <li style={styles.listItem}>Use automated systems (bots, scrapers) without permission</li>
            <li style={styles.listItem}>Reverse engineer or attempt to extract our source code</li>
            <li style={styles.listItem}>Share your account credentials with others</li>
            <li style={styles.listItem}>Circumvent any usage limits or restrictions</li>
          </ul>
          <p style={styles.paragraph}>
            If you become aware of suspected unlawful use, security abuse, or infringement involving the Service,
            please report it to <a href="mailto:support@scrolled.eu" style={styles.link}>support@scrolled.eu</a>.
          </p>
        </section>

        {/* Service Limitations */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>7. Service Limitations</h2>
          <p style={styles.paragraph}>
            <strong style={styles.strong}>7.1 AI-Generated Content</strong>
          </p>
          <p style={styles.paragraph}>
            Content generated by our AI is provided as an educational study aid. AI-generated content may contain
            errors, inaccuracies, or omissions, so you should verify important information against authoritative
            sources. This warning does not exclude any mandatory consumer guarantee or remedy.
          </p>

          <p style={styles.paragraph}>
            <strong style={styles.strong}>7.2 Usage Limits</strong>
          </p>
          <p style={styles.paragraph}>
            We may impose limits on document uploads, processing, or other features to ensure fair
            usage and service availability. These limits may vary based on your account type.
          </p>

          <p style={styles.paragraph}>
            <strong style={styles.strong}>7.3 Availability</strong>
          </p>
          <p style={styles.paragraph}>
            We do not guarantee that the Service will be available at all times. We may experience
            downtime for maintenance, updates, or unforeseen circumstances.
          </p>
        </section>

        {/* Privacy */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>8. Privacy</h2>
          <p style={styles.paragraph}>
            Your use of the Service involves processing described in our{' '}
            <a href="/privacy" style={styles.link}>Privacy Policy</a>, which explains how we collect,
            use, and protect your information and identifies the legal basis for each processing activity. Acceptance
            of these Terms is not blanket consent to every form of personal-data processing.
          </p>
        </section>

        {/* Disclaimers */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>9. Disclaimers (Service Provided "AS IS")</h2>
          <div style={styles.warningBox}>
            <p style={styles.paragraph}>
              <strong style={styles.strong}>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE SERVICE, PLATFORM,
              AND ALL AI-GENERATED STUDY MATERIALS, FLASHCARDS, QUIZZES, AND SUMMARIES ARE PROVIDED STRICTLY ON AN
              "AS IS", "AS AVAILABLE", AND "WITH ALL FAULTS" BASIS.</strong>
            </p>
            <p style={styles.paragraph}>
              WE EXPRESSLY DISCLAIM ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT
              NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, ACCURACY,
              NON-INFRINGEMENT, OR UNINTERRUPTED AVAILABILITY.
            </p>
            <p style={styles.paragraph}>
              AI-generated study outputs are produced automatically by third-party machine-learning models and may
              contain inaccuracies, omissions, or errors. AI outputs do not constitute professional, educational, legal,
              medical, or financial advice. You are solely responsible for independently verifying all AI-generated content
              against official and authoritative study sources.
            </p>
            <p style={{ ...styles.paragraph, marginBottom: 0 }}>
              Nothing in these Terms excludes, restricts, or overrides any guarantee, conformity requirement, remedy, or
              right that applicable consumer law makes mandatory. Any disclaimer applies only to the maximum extent
              permitted by law.
            </p>
          </div>
        </section>

        {/* Limitation of Liability */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>10. Limitation of Liability</h2>
          <p style={styles.paragraph}>
            To the maximum extent permitted by law, the ScrollEd operator will not be liable for indirect or
            consequential loss that was not reasonably foreseeable when you accepted these Terms, including:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Loss of profits, data, or goodwill</li>
            <li style={styles.listItem}>Service interruption or computer damage</li>
            <li style={styles.listItem}>Cost of substitute services</li>
            <li style={styles.listItem}>Loss caused by relying on AI output without reasonable verification</li>
          </ul>
          <p style={styles.paragraph}>
            Where applicable law permits a contractual cap for the claim in question, our aggregate liability shall
            not exceed the amount you have paid us in the twelve (12) months prior to the claim, or EUR 100,
            whichever is greater.
          </p>
          <p style={styles.paragraph}>
            This limitation does not apply where liability cannot lawfully be excluded or limited, including liability
            arising from fraud or fraudulent misrepresentation, wilful misconduct or gross negligence where the law
            so provides, death or personal injury caused by negligence, breach of mandatory consumer rights, or breach
            of data-protection obligations.
          </p>
        </section>

        {/* Indemnification */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>11. Indemnification</h2>
          <p style={styles.paragraph}>
            To the extent permitted by applicable law, you are responsible for direct loss finally incurred by the
            ScrollEd operator as a result of your intentional or reckless unlawful use of the Service, specifically:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Your violation of these Terms</li>
            <li style={styles.listItem}>Content you upload without the necessary rights or lawful authority</li>
            <li style={styles.listItem}>Your intentional infringement of third-party rights</li>
          </ul>
          <p style={styles.paragraph}>
            This section does not impose liability on a consumer beyond what applicable law permits and does not
            require a consumer to waive statutory rights or remedies.
          </p>
        </section>

        {/* Governing Law */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>12. Governing Law & Disputes</h2>
          <p style={styles.paragraph}>
            These Terms are governed by the law identified under applicable EU and national conflict-of-law rules.
            If you are a consumer, this does not deprive you of mandatory protections provided by the law of your
            habitual residence.
          </p>
          <p style={styles.paragraph}>
            Any disputes arising from these Terms or your use of the Service shall be resolved through
            good-faith negotiation where practical. If negotiation fails, either party may use any court or dispute
            mechanism competent under applicable law. No clause in these Terms forces an EU consumer to bring a claim
            exclusively in a foreign forum or prevents a consumer from using a court available under mandatory law.
          </p>
        </section>

        {/* Changes to Terms */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>13. Changes to Terms</h2>
          <p style={styles.paragraph}>
            We may modify these Terms to reflect legal, security, operational, or Service changes. For a material
            change, we will provide reasonable advance notice where required by law through one or more of the following:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Posting the updated Terms in the app</li>
            <li style={styles.listItem}>Updating the "Last Updated" date</li>
            <li style={styles.listItem}>Sending an email notification for material changes</li>
          </ul>
          <p style={styles.paragraph}>
            Changes will not retroactively remove accrued rights. Where applicable law requires express acceptance,
            the change will not bind you until that acceptance is obtained. Otherwise, you may stop using the Service
            and request account deletion before the change takes effect.
          </p>
        </section>

        {/* General contract provisions */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>14. General Provisions</h2>
          <p style={styles.paragraph}>
            <strong style={styles.strong}>Severability:</strong> If a provision is invalid or unenforceable, it will be
            limited or removed only to the minimum extent required, and the remaining provisions continue to apply.
            This does not replace or reduce mandatory consumer rights.
          </p>
          <p style={styles.paragraph}>
            <strong style={styles.strong}>No waiver:</strong> A delay or failure to enforce a provision is not a waiver
            of that provision or of the right to enforce it later.
          </p>
          <p style={styles.paragraph}>
            <strong style={styles.strong}>Transfer:</strong> You may not transfer your account in a way that compromises
            account security. The operator may transfer the Service agreement as part of a genuine reorganisation,
            sale, or transfer of the Service only where permitted by law and without reducing mandatory user rights;
            notice will be provided where required.
          </p>
          <p style={styles.paragraph}>
            <strong style={styles.strong}>Events outside reasonable control:</strong> Neither party is responsible for a
            failure or delay caused by an event outside its reasonable control, but this does not excuse payment already
            due, data-protection duties, reasonable mitigation, or liability that applicable law does not permit the
            parties to exclude.
          </p>
          <p style={styles.paragraph}>
            <strong style={styles.strong}>Entire agreement:</strong> These Terms and the documents expressly incorporated
            into them form the agreement concerning the Service, subject always to representations, remedies, and other
            rights that mandatory law does not permit the parties to exclude.
          </p>
          <p style={styles.paragraph}>
            Provisions concerning intellectual property, unlawful content, accrued payment or remedy rights, liability,
            disputes, and any term that by its nature must continue will survive account termination only for as long as
            reasonably necessary and legally permitted.
          </p>
        </section>

        {/* Contact */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>15. Contact Us</h2>
          <p style={styles.paragraph}>
            If you have any questions about these Terms, please contact us:
          </p>
          <div style={styles.infoBox}>
            <p style={{ ...styles.paragraph, marginBottom: '8px' }}>
              <strong style={styles.strong}>Email:</strong>{' '}
              <a href="mailto:support@scrolled.eu" style={styles.link}>support@scrolled.eu</a>
            </p>
            <p style={{ ...styles.paragraph, marginBottom: 0 }}>
              <strong style={styles.strong}>Website:</strong>{' '}
              <a href="https://scrolled.eu" target="_blank" rel="noopener noreferrer" style={styles.link}>
                scrolled.eu
              </a>
            </p>
          </div>
        </section>
      </main>

      <footer style={styles.footer}>
        <p>&copy; {new Date().getFullYear()} ScrollEd. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default TermsPage;
